/* eslint-disable max-lines */
import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import styled from 'styled-components'
import ArrowIcon from '@mui/icons-material/TrendingFlat'
import InfoIcon from '@mui/icons-material/Info'

import { Button, type ButtonStyles } from '@nexiahome/ui-common/components/primitives/buttons'

import PaywallAccountFields from 'pages/paywall/PaywallAccountFields'
import Checkbox from 'components/Checkbox'
import Spinner from 'components/Spinner'
import StatesAndProvinces from 'components/StatesDropdown/StatesAndProvinces'

import { isEmailValid, isUSZipValid, isCanadaPostalValid } from 'helpers/validations'
import WindowLocation from 'helpers/windowLocation'

import useActiveHehmAccount from 'hooks/useActiveHehmAccount'
import useDealer from 'hooks/useDealer'
import useDealerContactInfo from 'hooks/useDealerContactInfo'
import { useFeaturesContext } from 'hooks/FeaturesContext'
import useOracleAccount from 'hooks/useOracleAccount'
import useHehmTermsApi from 'hooks/useHehmTermsApi'

import Api from 'services/api'

import { color } from 'styles/colors'
import { StackedButtonContainer, SinglePanePage, PageTitle, screenSize } from 'styles/shared'

import { BillingData } from 'types/BillingData'

import HEHMTermsAndConditionsModal from './HEHMTermsAndConditionsModal'
import HEHMOracleInactiveModal from './HEHMOracleInactiveModal'
import Brand from 'helpers/brand'

const HEHMAccount = () => {
  const navigate = useNavigate()
  const { dealerId, planNo } = useParams()
  const { enabledFeatures } = useFeaturesContext()

  const { dealer: { channel }, isDealerLoaded } = useDealer(dealerId)
  const isDso = isDealerLoaded ? channel === 'DSO' : false
  const { dealerContactInfo, isDealerContactInfoLoaded } = useDealerContactInfo()
  const { activeHehmAccount, isActiveHehmAccountLoaded } = useActiveHehmAccount(dealerId)
  const { hehmTermsUpToDate, isHehmTermsLoaded } = useHehmTermsApi()
  const [paywallAcctAttributes, setPaywallAcctAttributes] = useState<BillingData>()
  const [agreementChecked, setAgreementChecked] = useState(false)
  const [displayTermsModal, setDisplayTermsModal] = useState(false)
  const { oracleAccount, isOracleAccountLoaded } = useOracleAccount(isDso, dealerId)
  const [errors, setErrors] = useState({ agreement: '', firstName: '', lastName: '', address: '', city: '', state: '', zip: '', email: '' })

  useEffect(() => {
    if (!isOracleAccountLoaded) return

    if (isDso && !oracleAccount?.active) {
      setPaywallAcctAttributes({
        firstName: '',
        lastName: '',
        address: '',
        address2: '',
        city: '',
        state: '',
        zip: '',
        email: '',
        country: '',
        isDso
      })
      return
    }

    if (isActiveHehmAccountLoaded && activeHehmAccount && isDealerLoaded) {
      setPaywallAcctAttributes({ ...activeHehmAccount.billingContact, isDso })
    } else if (isDealerContactInfoLoaded && dealerContactInfo && isDealerLoaded) {
      const useOracle = isDso && oracleAccount && oracleAccount.active
      const countryCode = findCountry(useOracle ? oracleAccount.address.state : dealerContactInfo.state)

      setPaywallAcctAttributes({
        firstName: '',
        lastName: '',
        address: (useOracle ? oracleAccount.address.addressLine1 : dealerContactInfo.address) || '',
        address2: (useOracle ? oracleAccount.address.addressLine2 : '') || '',
        city: (useOracle ? oracleAccount.address.city : dealerContactInfo.city) || '',
        state: (useOracle ? oracleAccount.address.state : dealerContactInfo.state) || '',
        zip: (useOracle ? oracleAccount.address.postalCode : dealerContactInfo.zip) || '',
        email: dealerContactInfo.email || '',
        country: (useOracle ? oracleAccount.address.countryCode : undefined) || countryCode || '',
        isDso
      })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealerContactInfo, isDealerContactInfoLoaded, isDealerLoaded, isActiveHehmAccountLoaded, activeHehmAccount, isOracleAccountLoaded])

  const findCountry = (stateCode: string = '') => {
    let countryCode = 'US'

    for (let i = 0; i < StatesAndProvinces.CAN_PROVINCES.length; i++) {
      if (StatesAndProvinces.CAN_PROVINCES[i].value === stateCode) {
        countryCode = 'CA'
        break
      }
    }

    return countryCode
  }

  const onChangeCallback = (updatedAttribute: Partial<BillingData>) => {
    setErrors(prev => ({
      ...prev,
      ...(updatedAttribute.firstName && { firstName: '' }),
      ...(updatedAttribute.lastName && { lastName: '' }),
      ...(updatedAttribute.address && { address: '' }),
      ...(updatedAttribute.city && { city: '' }),
      ...(updatedAttribute.state && { state: '' }),
      ...(updatedAttribute.zip && { zip: '' }),
      ...(updatedAttribute.email && { email: '' })
    }))

    if (updatedAttribute.state) updatedAttribute.country = findCountry(updatedAttribute.state)
    setPaywallAcctAttributes(prev => prev ? { ...prev, ...updatedAttribute } : prev)
  }

  const validateFields = () => {
    const newErrors = { agreement: '', firstName: '', lastName: '', address: '', city: '', state: '', zip: '', email: '' }
    const countryCode = findCountry(paywallAcctAttributes?.state) || 'US'

    if (!paywallAcctAttributes?.firstName) newErrors.firstName = 'First name is required'
    if (!paywallAcctAttributes?.lastName) newErrors.lastName = 'Last name is required'
    if (!paywallAcctAttributes?.address && !isDso) newErrors.address = 'Address is required'
    if (!paywallAcctAttributes?.city && !isDso) newErrors.city = 'City is required'
    if (!paywallAcctAttributes?.state && !isDso) newErrors.state = 'State is required'
    if (!paywallAcctAttributes?.zip && !isDso) newErrors.zip = 'Zip code is required'
    if (!paywallAcctAttributes?.email) newErrors.email = 'Email is required'
    if (paywallAcctAttributes?.email && !isEmailValid(paywallAcctAttributes.email)) newErrors.email = 'Invalid email'
    if (countryCode === 'CA' && paywallAcctAttributes?.zip && !isCanadaPostalValid(paywallAcctAttributes.zip)) newErrors.zip = 'Invalid postal code'
    if (countryCode === 'US' && paywallAcctAttributes?.zip && !isUSZipValid(paywallAcctAttributes.zip)) newErrors.zip = 'Invalid zip code'

    if (isDso && !agreementChecked) {
      newErrors.agreement = 'You must agree to being charged per installed kit in Oracle to proceed'
    }

    return newErrors
  }

  const onClickEnterCC = () => {
    const newErrors = validateFields()

    if (newErrors.firstName || newErrors.lastName || newErrors.address || newErrors.city || newErrors.state || newErrors.zip || newErrors.email) {
      setErrors(newErrors)
    } else if (paywallAcctAttributes) {
      setErrors(newErrors)

      Api.Paywall.triggerCybersource(Number(dealerId), Number(planNo), paywallAcctAttributes)
        .then(res => {
          WindowLocation.assign(res.postUrl + '/?' + (new URLSearchParams(res.cybersourceFields).toString()))
        }).catch()
    }
  }

  const onClickCreateAccount = () => {
    const newErrors = validateFields()

    if (newErrors.agreement || newErrors.firstName || newErrors.lastName || newErrors.email) {
      setErrors(newErrors)
    } else if (paywallAcctAttributes && dealerId) {
      setErrors(newErrors)

      Api.Paywall.create(dealerId, Number(planNo), { ...paywallAcctAttributes })
        .then(() => {
          navigate('/dashboard/ehmkt/accept')
        }).catch((error) => {
          navigate(`/dashboard/ehmkt/failure?error=${error.message}`)
        })
    }
  }

  const onCloseTermsModalCallback = () => {
    setDisplayTermsModal(false)
  }

  if (!isDealerContactInfoLoaded || !isDealerLoaded || !isActiveHehmAccountLoaded || !isHehmTermsLoaded || !isOracleAccountLoaded) {
    return <S.EmptyLoadingPage><Spinner variant="page" /></S.EmptyLoadingPage>
  }

  return (
    <S.Container>
      <S.Top>
        <Button decoration="link" onPress={() => navigate(-1)} aria-label="back-button">
          <S.ArrowIcon fontSize="large" />
        </Button>

        <S.ProductLogo src={Brand.hehmProductLogoPath()} alt="HEHM system logo" />
        <div />
      </S.Top>

      <SinglePanePage>
        <S.PageTitle>Billing Information</S.PageTitle>
        <S.Explanation>
          {isDso
            ? <>Please input your contact and billing information below.</>
            : (
              <>
                To update your credit card information, please enter your billing address and select
                <strong>&nbsp;Next&nbsp;</strong>
                to continue to payment details.
              </>
            )
          }
          <p><strong>&nbsp;You will be charged $6 plus applicable taxes per installed kit.&nbsp;</strong></p>
        </S.Explanation>
        <S.Form>
          <S.BillingAddressBox>
            <PaywallAccountFields {...{ ...paywallAcctAttributes, errors, onChangeCallback }} />
          </S.BillingAddressBox>

          <S.CreditCardDisclaimer>
            <S.InfoIcon fontSize="large" />
            <span>
              By adding your billing information, you agree to be charged monthly for each HVAC Performance Monitoring Kit that you install and provision. You may cancel individual subscriptions for your customers at any time.
              {!enabledFeatures?.includes('beta_tcs') && <> Please see our <S.TermsLink onClick={() => setDisplayTermsModal(true)}>Terms and Conditions</S.TermsLink> page for additional information.</>}
            </span>
          </S.CreditCardDisclaimer>

          {isDso && (
            <S.Checkbox
              label="I agree to being charged per installed kit in Oracle"
              checked={agreementChecked}
              onChange={() => setAgreementChecked(!agreementChecked)}
              errorMessage={errors.agreement}
              variant="dark"
            />
          )}

          <S.CreditCardNotChargedNote>
            Your {isDso ? 'account' : 'credit card'} will not be charged at this time.
          </S.CreditCardNotChargedNote>

          <S.ButtonContainer noBorder>
            <Button decoration="secondary" text="Back" styles={backButtonStyle} onPress={() => navigate(-1)} />
            <Button decoration="positive" text={isDso ? 'Activate' : 'Next'} styles={nextButtonStyle} onPress={() => isDso ? onClickCreateAccount() : onClickEnterCC()} />
          </S.ButtonContainer>
        </S.Form>
      </SinglePanePage>

      {displayTermsModal &&
        <HEHMTermsAndConditionsModal
          closeCallback={onCloseTermsModalCallback}
          hehmTermsUpToDate={hehmTermsUpToDate || false}
        />
      }

      {isDso && oracleAccount && !oracleAccount.active && (
        <HEHMOracleInactiveModal />
      )}
    </S.Container>
  )
}

export default HEHMAccount

const nextButtonStyle: ButtonStyles = {
  button: { width: 80, backgroundColor: color('billingButton') },
  text: { textTransform: 'capitalize' }
}

const backButtonStyle: ButtonStyles = {
  button: { width: 80, borderColor: color('billingButton') },
  text: { textTransform: 'capitalize', color: color('billingButton') }
}

const S = {
  Container: styled.div`
    height: 100%;
    background-color: ${color('background')};
  `,
  ArrowIcon: styled(ArrowIcon)`
    transform: rotate(180deg);
    margin-right: 10px;
    color: ${color('breadCrumbsTextHover')};
  `,
  Checkbox: styled(Checkbox)`
    margin-left: 30px;
    height: auto;
    flex-wrap: nowrap;

    > :nth-child(3) {
      flex-basis: 100%;
      margin-left: 0;
    }

    @media ${screenSize.small} {
      margin-left: 0px;
      justify-content: center;
    }
  `,
  CreditCardDisclaimer: styled.div`
    background-color: ${color('billingAddressBox')};
    margin: 20px auto;
    padding: 10px;
    border-radius: 10px;
    display: flex;
    align-items: center;

    @media ${screenSize.small} {
      padding: 20px;
    }
  `,
  CreditCardNotChargedNote: styled.div`
    text-align: right;

    @media ${screenSize.small} {
      text-align: center;
    }
  `,
  EmptyLoadingPage: styled(SinglePanePage)`
    height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
  `,
  InfoIcon: styled(InfoIcon)`
    margin-left: 10px;
    margin-right: 5px;
    color: ${color('questionMarkIcon')};
  `,
  ButtonContainer: styled(StackedButtonContainer)`
    margin-top: 0px;
    padding-top: 0px;

    @media ${screenSize.small} {
      margin-top: 20px;
      margin-bottom: 30px;
    }
  `,
  Explanation: styled.span`
    margin-bottom: 30px;
    text-align: left;
    color: ${color('checkboxText')};
    font-size: 12px;
  `,
  PageTitle: styled(PageTitle)`
    display: block;
    margin-top: 10px;
    margin-bottom: 10px;
    font-size: 24px;
    text-transform: capitalize;
  `,
  BillingAddressBox: styled.div`
    display: flex;
    flex-direction: column;
    background-color: ${color('billingAddressBox')};
    padding: 30px 40px;
    border-radius: 10px;

    @media ${screenSize.small} {
      padding: 20px;
    }
  `,
  Form: styled.form`
    margin-top: 20px;
    max-width: 1000px;
  `,
  ProductLogo: styled.img`
    height: 50px;
    @media ${screenSize.small} {
      max-width: 290px;
    }
  `,
  TermsLink: styled.a`
    cursor: pointer;
  `,
  Top: styled.div`
    display: flex;
    align-items: center;
    padding: 20px;
    margin-bottom: 32px;
    border-bottom: 1px solid ${color('border')};
  `
}
